import { collectRecordKeys } from '~/common/subRegions';

/** ISO string from the API, or an already-date-transformed luxon DateTime. */
type CreatedAt = string | { toISO(): string | null };

export interface ScopeRequestLike {
  id: string;
  agencySubGeometryId?: string | null;
  createdAt: CreatedAt;
}

export interface SiblingRequest extends ScopeRequestLike {
  requestId: string;
  type: string;
  requestStatus: string;
}

export interface ScopedSibling extends SiblingRequest {
  regionNumber: number;
  isCatchAll: boolean;
}

export interface RequestScope<S extends SiblingRequest = SiblingRequest> {
  regionNumber: number;
  regionCount: number;
  isCatchAll: boolean;
  siblings: (S & { regionNumber: number; isCatchAll: boolean })[];
}

/** CG-749: the "Region N of M" position string shared by the title, form, and banner. */
export function regionPositionLabel(scope: { regionNumber: number; regionCount: number }): string {
  return `Region ${scope.regionNumber} of ${scope.regionCount}`;
}

/**
 * CG-749: derive the sub-geometry split shape for the admin request-details banner.
 *
 * A request is part of a split when it and its siblings (same project + agency, active)
 * span more than one record key (`agencySubGeometryId ?? null`); the null key of a split
 * is the catch-all covering the remaining project area. Region numbering follows the
 * shared `collectRecordKeys` rule — distinct record keys in creation order (ties broken
 * by id), catch-all last — the same rule the customer-facing Utilities table uses, so
 * the two surfaces agree.
 *
 * Returns null when the request isn't part of a split (including when siblings are
 * unavailable — the banner is progressive enhancement).
 */
export function deriveRequestScope<S extends SiblingRequest>(
  self: ScopeRequestLike,
  siblings: S[] | undefined,
): RequestScope<S> | null {
  if (!siblings || siblings.length === 0) return null;

  const timeOf = (value: CreatedAt): number => {
    const ms = Date.parse(typeof value === 'string' ? value : (value.toISO() ?? ''));
    return Number.isNaN(ms) ? 0 : ms;
  };
  const inCreationOrder = [self, ...siblings].sort(
    (a, b) => timeOf(a.createdAt) - timeOf(b.createdAt) || a.id.localeCompare(b.id),
  );

  const recordKeys = collectRecordKeys(inCreationOrder);

  if (recordKeys.length <= 1) return null;

  const regionOf = (request: ScopeRequestLike) => recordKeys.indexOf(request.agencySubGeometryId ?? null) + 1;
  const isCatchAllOf = (request: ScopeRequestLike) => (request.agencySubGeometryId ?? null) === null;

  return {
    regionNumber: regionOf(self),
    regionCount: recordKeys.length,
    isCatchAll: isCatchAllOf(self),
    siblings: siblings.map((s) => ({ ...s, regionNumber: regionOf(s), isCatchAll: isCatchAllOf(s) })),
  };
}

/**
 * CG-749: caption under the Agency Info sub-header saying which scope the section's
 * contacts/private files/notes were resolved to. Data-driven (the backend always
 * resolves scope from the request's `agencySubGeometryId`): a scoped request gets the
 * sub-region caption; a catch-all inside a known split gets the agency-level caption;
 * a plain unsplit request gets none.
 */
export function scopedDataCaption(
  self: Pick<ScopeRequestLike, 'agencySubGeometryId'>,
  scope: RequestScope | null,
): string | null {
  if (self.agencySubGeometryId) {
    return 'The contacts, private files, and notes in this section are specific to this sub-region.';
  }
  if (scope) {
    return 'The contacts, private files, and notes in this section are agency-level.';
  }
  return null;
}
